import React from "react";
import { AlertTriangle, CheckCircle2, Plus, X } from "lucide-react";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { Label } from "@pgs-core/ui/components/label";
import { ColorPicker } from "@pgs-core/ui/components/color-picker";
import { contrastRatio } from "@pgs-core/ui/lib/color";
import {
  BASE_BACKGROUND_SLOT,
  MANDATORY_PALETTE_SLOTS,
  MAX_PALETTE_COLORS,
  PRIMARY_BRAND_SLOT,
} from "@pgs-core/portal-lib/palette";
import { A11Y_MIN_RATIO, TOKEN_DEFAULTS, type A11yTarget } from "../lib/design-tokens";
import { readableTextOn } from "../lib/derived-tokens";

/**
 * The calculated readable-text colour for a palette entry, shown as a live
 * "Aa" sample with its contrast ratio against the accessibility target.
 */
function CalculatedTextChip({ background, target }: { background: string; target: A11yTarget }) {
  const minRatio = A11Y_MIN_RATIO[target];
  const text = readableTextOn(background, minRatio);
  const ratio = contrastRatio(text, background);
  const pass = (ratio ?? 0) >= minRatio;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md border px-1.5 py-0.5 text-[11px] text-muted-foreground"
      title={`Calculated text colour ${text} — ${(ratio ?? 0).toFixed(2)}:1 against ${target.toUpperCase()} (≥${minRatio}:1)`}
    >
      <span
        className="flex h-5 w-7 items-center justify-center rounded border text-[11px] font-semibold"
        style={{ background, color: text }}
        aria-hidden
      >
        Aa
      </span>
      <span className="tabular-nums">{(ratio ?? 0).toFixed(1)}:1</span>
      {pass ? (
        <CheckCircle2 className="h-3 w-3 text-success" aria-label={`Meets ${target.toUpperCase()}`} />
      ) : (
        <AlertTriangle className="h-3 w-3 text-warning" aria-label={`Below ${target.toUpperCase()}`} />
      )}
    </span>
  );
}

/** One mandatory brand colour with its own labelled section. */
function MandatorySwatch({ label, description, value, fallback, target, onChange }: {
  label: string;
  description: string;
  value: string;
  fallback: string;
  target: A11yTarget;
  onChange: (value: string) => void;
}) {
  const effective = value.trim() || fallback;
  return (
    <div className="rounded-lg border p-3">
      <Label className="text-xs font-medium">{label}</Label>
      <p className="mb-2 mt-0.5 text-xs text-muted-foreground">{description}</p>
      <div className="flex items-center gap-2">
        <ColorPicker value={effective} onChange={onChange} ariaLabel={`${label} picker`} />
        <Input
          value={value}
          placeholder={fallback}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          className="font-mono text-xs"
        />
        <CalculatedTextChip background={effective} target={target} />
      </div>
    </div>
  );
}

/**
 * The tenant's brand palette. Slots 1 and 2 are mandatory (Primary Brand
 * Colour and Base Background Colour) and rendered as distinct sections;
 * further colours are optional extras. Every entry shows its calculated
 * readable-text colour. The palette also feeds every picker's Presets row
 * across the portals (design tokens, section backgrounds, …).
 */
export function PaletteEditor({ palette, target, onChange }: {
  palette: string[];
  target: A11yTarget;
  onChange: (palette: string[]) => void;
}) {
  const setColor = (index: number, value: string) => {
    const next = palette.slice();
    while (next.length <= index) next.push("");
    next[index] = value;
    onChange(next);
  };
  const remove = (index: number) => {
    if (index < MANDATORY_PALETTE_SLOTS) return;
    onChange(palette.filter((_, i) => i !== index));
  };
  const add = () => {
    if (palette.length < MAX_PALETTE_COLORS) onChange([...palette, "#2563eb"]);
  };
  const extras = palette.slice(MANDATORY_PALETTE_SLOTS);

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <MandatorySwatch
          label="Primary Brand Colour"
          description="The signature colour — drives the accent and hero background unless overridden."
          value={palette[PRIMARY_BRAND_SLOT] ?? ""}
          fallback={TOKEN_DEFAULTS.accent}
          target={target}
          onChange={(value) => setColor(PRIMARY_BRAND_SLOT, value)}
        />
        <MandatorySwatch
          label="Base Background Colour"
          description="The page background — body and muted text colours are calculated against it."
          value={palette[BASE_BACKGROUND_SLOT] ?? ""}
          fallback={TOKEN_DEFAULTS.bg}
          target={target}
          onChange={(value) => setColor(BASE_BACKGROUND_SLOT, value)}
        />
      </div>

      <div>
        <Label className="text-xs font-medium">Additional colours</Label>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {extras.map((color, i) => {
            const index = i + MANDATORY_PALETTE_SLOTS;
            return (
              <div key={index} className="group relative flex items-center gap-1.5 rounded-md border p-1.5">
                <ColorPicker
                  value={color}
                  onChange={(value) => setColor(index, value)}
                  ariaLabel={`Palette colour ${index + 1}`}
                />
                <CalculatedTextChip background={color} target={target} />
                <button
                  type="button"
                  aria-label={`Remove palette colour ${index + 1}`}
                  onClick={() => remove(index)}
                  className="absolute -right-1.5 -top-1.5 z-10 hidden h-4 w-4 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm hover:text-destructive group-hover:flex"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
          <Button size="sm" variant="outline" onClick={add} disabled={palette.length >= MAX_PALETTE_COLORS}>
            <Plus className="h-3.5 w-3.5" />
            Add colour
          </Button>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          Preset colours offered in every colour picker — here, and in the client's page editor.
        </p>
      </div>
    </div>
  );
}
